/**
 * The Recent Activity tray (migration 0012) — "where did that item land, and what did AI do
 * to it," across every path a submission can take: still pending in Inbox, auto-filed by the
 * routing prompts, or enriched after being created already-routed. Answers the thing Inbox
 * itself never could, since Inbox only ever shows what's still waiting on a human decision —
 * once something's filed, this is the one place left to go looking for it.
 *
 * A dismissible box, not a notification — nothing here pops up or interrupts; entries just
 * accumulate until glanced at and dismissed, same posture as `InboxCard`. `landed` names
 * where a submission ended up, and for a task or note it opens that item's editor in a popup
 * (`EditPopupProvider`, #82) — the one way to reach something a watch sent in and the AI filed
 * while no Inbox result was on screen to tell you.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type ActivityItem } from "../api";
import { openFiled, useEditPopups } from "../editPopupContext";
import { useEditMode } from "../layout/editModeContext";
import { Placeholder } from "../layout/LayoutGrid";
import { AiReasoningDisclosure } from "./AiReasoningDisclosure";

const LANDED_LABEL: Record<ActivityItem["landed"]["type"], string> = {
  task: "Task",
  note: "Note",
  area: "New area",
  project: "New project",
  context: "Context",
  inbox: "Still in Inbox",
  discarded: "Discarded",
};

/** Landings with somewhere to open: a popup for a task or note, the page a project or Context
 * entry is edited on (`openFiled`). */
const OPENABLE = new Set(["task", "note", "project", "context"]);

const FIELD_LABEL: Record<string, string> = {
  title: "title",
  notes: "notes",
  body: "text",
  energy: "energy",
  due: "due date",
  starred: "starred",
  important: "important",
  area_id: "area",
  project_id: "project",
};

function describeFields(fields: Record<string, unknown>): string {
  const keys = Object.keys(fields);
  if (keys.length === 0) return "";
  return keys.map((k) => FIELD_LABEL[k] ?? k).join(", ");
}

export function ActivityCard({
  refreshKey,
  onChange,
}: {
  refreshKey?: number;
  onChange?: () => void | Promise<void>;
}) {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { editing } = useEditMode();
  const popups = useEditPopups();
  // What the last successful fetch already had, so a poll can tell "AI actually produced
  // something new" apart from "nothing changed, same answer as last time" — `null` means
  // no fetch has landed yet (the initial mount load shouldn't compare against nothing and
  // conclude everything on it is new). Keyed the same way the list below is.
  const seenIds = useRef<Set<string> | null>(null);

  const load = useCallback(async () => {
    try {
      const fetched = (await api.activity()).items;
      setItems(fetched);
      setError(null);

      const nextIds = new Set(fetched.map((item) => `${item.kind}-${item.id}`));
      // A task/note this card knows about (title rewritten, energy/due/starred filled in)
      // is a task/note other cards on the page — TasksCard chief among them — are showing
      // with now-stale fields, and nothing else would ever tell them to refetch: the
      // enrichment worker runs entirely server-side, with no way to push. Bumping here,
      // only when something genuinely new appeared (never on a routine "nothing changed"
      // poll), is what closes that gap without every poll refreshing the whole page.
      if (seenIds.current !== null) {
        const hasNew = fetched.some((item) => !seenIds.current!.has(`${item.kind}-${item.id}`));
        if (hasNew) void onChange?.();
      }
      seenIds.current = nextIds;
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return;
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [onChange]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  // Results arrive off-request, from parse_worker.tick() (backend, every 20s) — polling
  // faster than that cadence would only re-fetch the same answer, so this matches it rather
  // than chasing freshness the source doesn't have. Deliberately its own effect, calling
  // this card's own `load()` directly rather than `onChange` — a routine "nothing changed"
  // poll (most of them) must never bump the page-level refreshKey every other card on
  // Briefing shares, which would refetch all of them and re-render state (undo windows
  // among them) that has nothing to do with this card. `load()` itself still calls
  // `onChange` on the rarer poll that finds something genuinely new — see its own comment.
  // Paused while the tab is hidden, and catches up immediately when it isn't, rather than a
  // fixed-cadence poll wasting requests on a backgrounded tab.
  useEffect(() => {
    const POLL_INTERVAL_MS = 25_000;
    const interval = setInterval(() => {
      if (!document.hidden) void load();
    }, POLL_INTERVAL_MS);
    function onVisibilityChange() {
      if (!document.hidden) void load();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [load]);

  async function dismiss(item: ActivityItem) {
    setItems((current) => current.filter((i) => i.id !== item.id));
    try {
      await api.dismissActivity(item.kind, item.id);
      await onChange?.();
    } catch (err) {
      // Put it back — the dismiss didn't actually take.
      setError(err instanceof Error ? err.message : String(err));
      void load();
    }
  }

  /** Clears what is on screen. Only those entries: one that lands after the last poll has not been
   * seen yet, so it stays. */
  async function dismissAll() {
    const shown = items.map(({ kind, id }) => ({ kind, id }));
    setItems([]);
    try {
      await api.dismissActivities(shown);
      await onChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      void load();
    }
  }

  if (items.length === 0 && !error) return editing ? <Placeholder id="activity" /> : null;

  return (
    <section className="panel">
      <header>
        <h2>Recent activity</h2>
        {items.length > 1 && (
          <button className="btn ghost small" type="button" onClick={() => void dismissAll()}>
            Dismiss all
          </button>
        )}
      </header>

      {error && <p className="error">{error}</p>}

      {items.length === 0 ? null : (
        <ul className="activity-list" style={{ listStyle: "none", padding: 0, display: "grid", gap: "0.6rem" }}>
          {items.map((item) => {
            const filledText = describeFields(item.filled);
            const droppedTier = item.tier && item.preferred_tier && item.tier !== item.preferred_tier;
            return (
              <li key={`${item.kind}-${item.id}`} className="activity-item">
                <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div>{item.raw_text}</div>
                    <div className="small muted">
                      {item.kind === "refined"
                        ? `${item.landed.type === "project" ? "Project" : LANDED_LABEL[item.landed.type]} edited by AI`
                        : LANDED_LABEL[item.landed.type]}
                      :{" "}
                      {OPENABLE.has(item.landed.type) ? (
                        // Opens the item itself, which is the point of this card: it is where
                        // you find out what the AI did, and now where you fix it.
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => openFiled(popups, item.landed.type, item.landed.id)}
                          aria-label={`Open ${LANDED_LABEL[item.landed.type].toLowerCase()}: ${item.landed.title}`}
                        >
                          {item.landed.title}
                        </button>
                      ) : (
                        item.landed.title
                      )}
                      {filledText && ` - AI set ${filledText}`}
                    </div>
                    {droppedTier && (
                      <div className="small muted">
                        Ran at {item.tier} - {item.preferred_tier} was busy
                      </div>
                    )}
                  </div>
                  <button
                    className="btn ghost small"
                    type="button"
                    onClick={() => void dismiss(item)}
                    aria-label="Dismiss"
                  >
                    Dismiss
                  </button>
                </div>
                <AiReasoningDisclosure
                  reasoning={{ reason: item.reason, model_key: item.model_key, slot: item.tier }}
                />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
